import type { ThreadMessage } from '../types';

export const MANUAL_REPLY_RECENCY_MS = 30 * 60 * 1000;

type RelevantActivity = 'inbound' | 'manual_outbound' | 'other_outbound' | 'manual_marker';

function parseFirstTimestamp(...values: (string | null | undefined)[]): number | null {
  for (const value of values) {
    if (!value) continue;
    const parsed = Date.parse(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

/**
 * Returns whether the latest relevant thread activity is a recent manual reply.
 *
 * Imported history can contain `manual_linkedin` outbound rows, so their
 * original `sentAt` must be inside the pause window. A newer inbound (or a
 * different outbound source) supersedes the manual reply and unblocks the AI.
 */
export function deriveManualReplyDetected(messages: ThreadMessage[], now = Date.now()): boolean {
  let latest: { activity: RelevantActivity; at: number; index: number } | null = null;

  for (const [index, message] of messages.entries()) {
    let activity: RelevantActivity | null = null;
    let at: number | null = null;

    if (message.role === 'in') {
      activity = 'inbound';
      at = parseFirstTimestamp(message.receivedAt, message.createdAt);
    } else if (message.role === 'out') {
      activity =
        message.outboundSource === 'manual_linkedin' ? 'manual_outbound' : 'other_outbound';
      at = parseFirstTimestamp(message.sentAt, message.createdAt);
    } else if (message.body === 'system.manual_reply_detected') {
      activity = 'manual_marker';
      at = parseFirstTimestamp(message.createdAt);
    }

    if (activity === null || at === null) continue;
    if (latest === null || at > latest.at || (at === latest.at && index > latest.index)) {
      latest = { activity, at, index };
    }
  }

  if (latest === null || latest.at < now - MANUAL_REPLY_RECENCY_MS) return false;
  return latest.activity === 'manual_outbound' || latest.activity === 'manual_marker';
}
